import React, { useEffect, useState, useCallback } from "react";
import { displayName } from "@/lib/names";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { dateLabel, lifeLabel } from "@/lib/dates";
import { LoadError } from "@/components/LoadError";
import { PublicPersonList } from "@/components/PublicPersonList";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { PersonAvatar } from "@/components/PersonAvatar";
import { PersonForm } from "@/components/PersonForm";
import { VerificationBadge } from "@/components/VerificationBadge";
import { ProvisionalTag } from "@/components/PersonPicker";
import { FounderSection } from "@/components/FounderSection";
import { JournalSection } from "@/components/JournalSection";
import { Plus, Search, Edit3, Trash2, UserPlus, ArrowLeft, Printer } from "lucide-react";
import { toast } from "sonner";

export const ClansIndex = () => {
  const { API } = useAuth();
  const { t, lang } = useLang();
  const [clans, setClans] = useState([]);

  useEffect(() => {
    (async () => {
      const { data } = await axios.get(`${API}/clans`);
      setClans(data);
    })();
  }, [API]);

  return (
    <div className="max-w-7xl mx-auto px-6 sm:px-10 py-10" data-testid="clans-index">
      <div className="mb-10 fade-in-up">
        <div className="label-uppercase text-[#993D22] mb-2">{t("clans")}</div>
        <h1 className="font-serif-heritage text-5xl text-[#261911]">
          {lang === "fr" ? "Les cinq familles Mvog Koé" : "The five Mvog Koé families"}
        </h1>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 stagger">
        {clans.map(c => (
          <Link to={`/clans/${c.key}`} key={c.key} className="heritage-card p-6 group" data-testid={`clan-item-${c.key}`}>
            <div className="w-16 h-16 rounded-full flex items-center justify-center text-[#FAF7F2] font-serif-heritage text-2xl font-bold mb-4" style={{ background: c.color }}>
              {c.name.split(" ").map(w => w[0]).join("").slice(0, 2)}
            </div>
            <h3 className="font-serif-heritage text-3xl text-[#261911] mb-2">{c.name}</h3>
            <p className="text-sm text-[#6E5B4F] mb-4">{lang === "fr" ? c.description : c.description_en}</p>
            <div className="flex items-center justify-between text-xs">
              <span className="label-uppercase text-[#6E5B4F]">{c.member_count} {t("members")}</span>
              <span className="text-[#993D22] font-semibold group-hover:translate-x-1 transition-transform">{t("viewClan")} →</span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

export const ClanDetailPage = () => {
  const { key } = useParams();
  const { API, user } = useAuth();
  const { t, lang } = useLang();
  const nav = useNavigate();
  const [clan, setClan] = useState(null);
  const [persons, setPersons] = useState([]);
  const [allPersons, setAllPersons] = useState([]);
  const [clans, setClans] = useState([]);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("__all");
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const [loadError, setLoadError] = useState(false);
  const isMember = user && (user.validated || user.is_super_admin);
  const load = useCallback(async () => {
    setLoadError(false);
    const opt = { timeout: 20000 };
    try {
      const [cl, clist] = await Promise.all([axios.get(`${API}/clans/${key}`, opt), axios.get(`${API}/clans`, opt)]);
      setClan(cl.data);
      setClans(clist.data);
    } catch { setLoadError(true); return; }
    if (!isMember) return;
    const [ps, all] = await Promise.all([
      axios.get(`${API}/persons`, { params: { clan_key: key }, ...opt }).catch(() => ({ data: [] })),
      axios.get(`${API}/persons`, opt).catch(() => ({ data: [] })),
    ]);
    setPersons(ps.data);
    setAllPersons(all.data);
  }, [API, key, isMember]);

  useEffect(() => { load(); }, [load]);

  const filtered = persons.filter(p => {
    if (statusFilter === "__provisional") { if (!p.is_provisional) return false; }
    else if (statusFilter !== "__all" && (p.verification_status || "to_verify") !== statusFilter) return false;
    if (!query) return true;
    const q = query.toLowerCase();
    return `${displayName(p)} ${p.nickname}`.toLowerCase().includes(q);
  });

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/persons/${id}`);
      toast.success(t("deleteSuccess"));
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  if (loadError) return <LoadError onRetry={load} />;
  if (!clan) return <div className="p-10 text-center text-[#6E5B4F]" data-testid="clan-loading">{t("loading")}</div>;

  const isLogged = !!isMember;
  const isAdmin = user?.is_super_admin || user?.role === "admin";

  return (
    <div className="max-w-7xl mx-auto px-6 sm:px-10 py-10" data-testid="clan-detail-page">
      <button onClick={() => nav("/clans")} className="text-sm text-[#993D22] flex items-center gap-1 mb-6" data-testid="back-to-clans-btn">
        <ArrowLeft className="w-4 h-4" /> {t("clans")}
      </button>

      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-10">
        <div className="flex items-start gap-5">
          <div className="w-20 h-20 rounded-full flex items-center justify-center text-[#FAF7F2] font-serif-heritage text-3xl font-bold shrink-0" style={{ background: clan.color }}>
            {clan.name.split(" ").map(w => w[0]).join("").slice(0, 2)}
          </div>
          <div>
            <div className="label-uppercase mb-1" style={{ color: clan.color }}>{t("clan")}</div>
            <h1 className="font-serif-heritage text-5xl text-[#261911] mb-2">{clan.name}</h1>
            <p className="text-[#6E5B4F] max-w-2xl">{lang === "fr" ? clan.description : clan.description_en}</p>
          </div>
        </div>
        {isLogged && (
          <div className="flex flex-wrap items-center gap-2">
            <Button
              onClick={() => window.open(`/print/clans/${key}`, "_blank")}
              variant="outline"
              className="border-[#6B4226] text-[#6B4226]"
              data-testid="print-clan-btn"
            >
              <Printer className="w-4 h-4 mr-2" /> {t("printClan")}
            </Button>
            <Button onClick={() => { setEditing({ clan_key: key }); setOpen(true); }} className="btn-terra" data-testid="add-person-btn">
              <UserPlus className="w-4 h-4 mr-2" /> {t("addPerson")}
            </Button>
          </div>
        )}
        {!isLogged && (
          <Button
            onClick={() => window.open(`/print/clans/${key}`, "_blank")}
            variant="outline"
            className="border-[#6B4226] text-[#6B4226]"
            data-testid="print-clan-btn-public"
          >
            <Printer className="w-4 h-4 mr-2" /> {t("printClan")}
          </Button>
        )}
      </div>

      <FounderSection
        clan={clan}
        canEdit={isLogged && (user?.role === "elder" || user?.role === "admin")}
        onChanged={load}
      />

      {isLogged && (
        <JournalSection
          clan={clan}
          canEdit={isLogged && (user?.role === "elder" || user?.role === "admin")}
        />
      )}
      {!isLogged && (
        <div className="heritage-card p-6 mb-8 text-[#3A2312]" data-testid="members-only-notice">
          <p className="font-serif-heritage text-xl mb-1">Les personnes recensées dans cette famille</p>
          <p className="text-sm text-[#6E5B4F]">Fiches en lecture seule. Pour protéger la vie privée, seules les informations autorisées sont affichées, et les personnes vivantes apparaissent sans dates ni photo. <a href="/login" className="text-[#993D22] underline">Se connecter</a> · <a href="/register" className="text-[#993D22] underline">Demander un accès</a> · <a href={`/histoire?rubrique=famille_${clan.key}`} className="text-[#993D22] underline">Lire l'histoire de cette famille</a></p>
        </div>
      )}
      {!isLogged && <PublicPersonList clanKey={key} />}

      {isLogged && (<>
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6E5B4F]" />
          <Input value={query} onChange={e => setQuery(e.target.value)} placeholder={t("search")} className="pl-10 bg-[#FAF7F2]" data-testid="search-input" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48 bg-[#FAF7F2]" data-testid="status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">{t("allStatuses")}</SelectItem>
            <SelectItem value="to_verify">{t("to_verify")}</SelectItem>
            <SelectItem value="testimony_confirmed">{t("testimony_confirmed")}</SelectItem>
            <SelectItem value="cross_validated">{t("cross_validated")}</SelectItem>
            <SelectItem value="__provisional">Fiches provisoires</SelectItem>
          </SelectContent>
        </Select>
        <div className="label-uppercase text-[#6E5B4F] ml-auto">{filtered.length} {t("members")}</div>
      </div>

      {filtered.length === 0 ? (
        <div className="heritage-card p-10 text-center text-[#6E5B4F]" data-testid="empty-members">
          <p className="font-serif-heritage text-xl">{t("noPersons")}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 stagger">
          {filtered.map(p => (
            <div key={p.id} className="heritage-card p-5 flex flex-col gap-3" data-testid={`person-card-${p.id}`}>
              <div className="flex items-center gap-3">
                <PersonAvatar person={p} size={56} />
                <div className="flex-1 min-w-0">
                  <Link to={`/persons/${p.id}`} className="font-serif-heritage text-xl text-[#261911] hover:text-[#993D22] block truncate">
                    {displayName(p)}
                  </Link>
                  {p.nickname && <div className="text-xs italic text-[#6E5B4F]">« {p.nickname} »</div>}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="text-xs text-[#6E5B4F] label-uppercase">
                  {p.generation ? `G${p.generation} · ` : ""}{p.birth_year ? lifeLabel(p) : "date inconnue"}
                </div>
                <span>{p.is_provisional && <ProvisionalTag />} <VerificationBadge status={p.verification_status || "to_verify"} withLabel={false} /></span>
              </div>
              {p.biography && <p className="text-sm text-[#3A2312] line-clamp-2">{p.biography}</p>}
              {isLogged && (
                <div className="flex items-center justify-end gap-1 pt-2 border-t border-[#E3D5CA]">
                  <Button size="sm" variant="ghost" onClick={() => { setEditing(p); setOpen(true); }} data-testid={`edit-${p.id}`}>
                    <Edit3 className="w-3.5 h-3.5" />
                  </Button>
                  {isAdmin && <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="sm" variant="ghost" className="text-red-700 hover:text-red-800" data-testid={`delete-${p.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>{t("confirmDelete")}</AlertDialogTitle>
                        <AlertDialogDescription>{displayName(p)}</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
                        <AlertDialogAction onClick={() => handleDelete(p.id)} className="bg-red-700 hover:bg-red-800">{t("delete")}</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      </>)}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#FAF7F2] max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif-heritage text-2xl text-[#261911]">
              {editing?.id ? t("editPerson") : t("newPerson")}
            </DialogTitle>
          </DialogHeader>
          <PersonForm
            initial={editing}
            clans={clans}
            allPersons={allPersons}
            onDone={() => { setOpen(false); load(); }}
            onCancel={() => setOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};
